import { useEffect, useRef } from "react";
import TiltCard from "@/components/base/TiltCard";
import { SITE_MEDIA } from "@/constants/siteMedia";

const problemCards = [
  {
    icon: "ri-moon-cloudy-line",
    iconColor: "#7C3AED",
    iconBg: "#ede8ff",
    barColor: "#7C3AED",
    title: "Overwhelmed and can't sleep",
    description: "It's 2 AM and your mind won't stop racing. You need to vent, but everyone you know is asleep.",
    image: SITE_MEDIA.problem1,
  },
  {
    icon: "ri-heart-line",
    iconColor: "#e11d48",
    iconBg: "#fff1f2",
    barColor: "#e11d48",
    title: "Feeling alone, even with people around",
    description: "You're surrounded by others but still feel invisible. No one really sees what you're going through.",
    image: SITE_MEDIA.problem2,
  },
  {
    icon: "ri-smartphone-line",
    iconColor: "#d97706",
    iconBg: "#fff8e8",
    barColor: "#d97706",
    title: "No one to talk to — or no one you trust",
    description: "You want to open up, but you fear judgment. Sometimes the people closest to you are the hardest to talk to.",
    image: SITE_MEDIA.problem3,
  },
  {
    icon: "ri-heart-pulse-line",
    iconColor: "#059669",
    iconBg: "#f0fdf4",
    barColor: "#059669",
    title: "Needing support without being fixed",
    description: "You don't want advice or solutions. You just need someone to listen, understand, and simply be there.",
    image: SITE_MEDIA.problem4,
  },
];

export default function Problem() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("problem-visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const items = sectionRef.current?.querySelectorAll(".problem-animate");
    items?.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="problem" className="py-14 md:py-20 bg-white">
      <style>{`
        .problem-animate {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .problem-animate.problem-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .problem-animate:nth-child(1) { transition-delay: 0s; }
        .problem-animate:nth-child(2) { transition-delay: 0.08s; }
        .problem-animate:nth-child(3) { transition-delay: 0.16s; }
        .problem-animate:nth-child(4) { transition-delay: 0.24s; }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12 problem-animate">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sparkling-line text-xs"></i>
            </div>
            THE PROBLEM
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            Sometimes, you just need
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              someone to talk to.
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-xl mx-auto leading-relaxed mb-2">
            Life doesn't always wait for the right moment.
          </p>
          <p className="text-[#5a4a8a] text-base max-w-xl mx-auto leading-relaxed mb-2">
            Sometimes you're overwhelmed. Sometimes you just need to think things through.
          </p>
          <p className="text-[#5a4a8a] text-base max-w-xl mx-auto leading-relaxed mb-6">
            And sometimes, there's no one available — or no one you feel comfortable opening up to.
          </p>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#e11d48]/20 bg-[#fff1f2] text-[#e11d48] text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-[#e11d48] inline-block"></span>
            That's where Ava comes in.
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {problemCards.map((card, i) => (
            <TiltCard
              key={i}
              className="problem-animate"
              tiltAmount={8}
              scale={1.02}
            >
              <div className="rounded-3xl overflow-hidden border border-[#ede8ff] bg-white group cursor-default">
                {/* Image */}
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
                  <div
                    className="absolute top-3 left-3 w-9 h-9 flex items-center justify-center rounded-xl border"
                    style={{ background: card.iconBg, borderColor: card.iconColor + "30" }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${card.icon} text-sm`} style={{ color: card.iconColor }}></i>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-5 h-5 flex items-center justify-center rounded-md"
                      style={{ background: card.iconBg }}
                    >
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${card.icon} text-xs`} style={{ color: card.iconColor }}></i>
                      </div>
                    </div>
                    <h3 className="font-semibold text-[#1a1040] text-sm leading-snug">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-[#7a6aaa] text-xs leading-relaxed">{card.description}</p>
                  <div
                    className="mt-4 h-[3px] rounded-full"
                    style={{ background: card.barColor, width: "40px" }}
                  />
                </div>
              </div>
            </TiltCard>
          ))}
        </div>

        {/* Bottom text */}
        <div className="text-center problem-animate">
          <p className="text-[#5a4a8a] text-base mb-3">
            These moments are real. And they deserve a real response.
          </p>
        </div>
      </div>
    </section>
  );
}